// WYHSIAR GAMES WORLD - FRIV-STYLE ARCADE CONTROLLER (20 Online Multiplayer Games)
import { io } from 'socket.io-client';
import { sounds } from './sound.js';
import { AvatarManager } from './avatar.js';
import { ChatManager } from './chat.js';
import { LocalSocketBridge } from './localBridge.js';
import { P2PNetworkBridge } from './p2pBridge.js';

// 14 Original Mini-Games Clients
import { DrawGuessClient } from './games/drawGuess.js';
import { SpyGameClient } from './games/spyGame.js';
import { WordBombClient } from './games/wordBomb.js';
import { SnakeArenaClient } from './games/snakeArena.js';
import { Connect4Client } from './games/connect4.js';
import { CyberPongClient } from './games/cyberPong.js';
import { UnoCardClient } from './games/unoCard.js';
import { SpaceSurvivalClient } from './games/spaceSurvival.js';
import { LudoGameClient } from './games/ludoGame.js';
import { TankGameClient } from './games/tankGame.js';
import { TriviaGameClient } from './games/triviaGame.js';
import { TetrisGameClient } from './games/tetrisGame.js';
import { KartGameClient } from './games/kartGame.js';
import { TicTacToeClient } from './games/tictactoeGame.js';

// 6 New Mini-Games Clients
import { PoolGameClient } from './games/poolGame.js';
import { BombTagGameClient } from './games/bombTagGame.js';
import { SoccerGameClient } from './games/soccerGame.js';
import { BattleshipGameClient } from './games/battleshipGame.js';
import { AirHockeyGameClient } from './games/airHockeyGame.js';
import { ArcheryGameClient } from './games/archeryGame.js';

// 1. DOM Views
const viewHub = document.getElementById('view-hub');
const viewRoom = document.getElementById('view-room');
const viewGame = document.getElementById('view-game');

// Hub Elements
const gameGridContainer = document.getElementById('game-grid-container');
const btnCreateRoom = document.getElementById('btn-create-room');
const btnJoinRoom = document.getElementById('btn-join-room');
const inputJoinCode = document.getElementById('input-join-code');
const hubAvatar = document.getElementById('hub-avatar');
const hubName = document.getElementById('hub-name');
const btnOpenProfile = document.getElementById('btn-open-profile');
const inputGameSearch = document.getElementById('input-game-search');
const btnClearSearch = document.getElementById('btn-clear-search');
const btnSoundToggle = document.getElementById('btn-sound-toggle');
const soundIcon = document.getElementById('sound-icon');
const btnFullscreenToggle = document.getElementById('btn-fullscreen-toggle');

// Game Preview Spotlight Modal Elements
const gamePreviewModal = document.getElementById('game-preview-modal');
const previewBackdrop = document.getElementById('preview-backdrop');
const btnClosePreview = document.getElementById('btn-close-preview');
const previewIcon = document.getElementById('preview-icon');
const previewBadge = document.getElementById('preview-badge');
const previewTitle = document.getElementById('preview-title');
const previewCategory = document.getElementById('preview-category');
const previewPlayers = document.getElementById('preview-players');
const previewDesc = document.getElementById('preview-desc');
const previewControls = document.getElementById('preview-controls');
const selectModalPlayers = document.getElementById('select-modal-players');
const checkModalBots = document.getElementById('check-modal-bots');
const btnPreviewInstantPlay = document.getElementById('btn-preview-instant-play');
const btnPreviewPartyRoom = document.getElementById('btn-preview-party-room');

// Room Elements
const roomDisplayCode = document.getElementById('room-display-code');
const btnCopyRoomLink = document.getElementById('btn-copy-room-link');
const btnWhatsappRoomLink = document.getElementById('btn-whatsapp-room-link');
const roomCountBadge = document.getElementById('room-count-badge');
const seatsContainer = document.getElementById('seats-container');
const selectorCardsRow = document.getElementById('selector-cards-row');
const btnStartSelectedGame = document.getElementById('btn-start-selected-game');
const btnReadyToggle = document.getElementById('btn-ready-toggle');
const btnLeaveRoom = document.getElementById('btn-leave-room');
const hostNotice = document.getElementById('host-notice');
const roomLayout = document.querySelector('.room-layout');
const roomMobileTabs = document.getElementById('room-mobile-tabs');
const mobileChatBadge = document.getElementById('mobile-chat-badge');

// Quick Play Elements
const btnQuickPlay = document.getElementById('btn-quick-play');
const quickplayModal = document.getElementById('quickplay-modal');
const quickplayBackdrop = document.getElementById('quickplay-backdrop');
const quickplayCountdown = document.getElementById('quickplay-countdown');
const quickplayStatusText = document.getElementById('quickplay-status-text');
const btnCancelQuickplay = document.getElementById('btn-cancel-quickplay');

// Toast Floating Layer
const toastContainer = document.getElementById('toast-container');

// Host Settings Elements
const hostSettingsPanel = document.getElementById('host-settings-panel');
const selectRoomPlayers = document.getElementById('select-room-players');
const selectRoomBots = document.getElementById('select-room-bots');
const selectTurnTime = document.getElementById('select-turn-time');
const selectTotalRounds = document.getElementById('select-total-rounds');

// Game Viewport Elements
const btnBackToRoom = document.getElementById('btn-back-to-room');
const btnGameSoundToggle = document.getElementById('btn-game-sound-toggle');
const btnGameFullscreen = document.getElementById('btn-game-fullscreen');
const btnInGameInvite = document.getElementById('btn-in-game-invite');
const btnInGameWhatsapp = document.getElementById('btn-in-game-whatsapp');
const activeGameIcon = document.getElementById('active-game-icon');
const activeGameTitle = document.getElementById('active-game-title');
const activeGameControlsHint = document.getElementById('active-game-controls-hint');
const activeGameContainer = document.getElementById('active-game-container');

// Profile Modal Elements
const profileModal = document.getElementById('profile-modal');
const btnCloseProfile = document.getElementById('btn-close-profile');
const profNameInput = document.getElementById('prof-name-input');
const avatarsPickerContainer = document.getElementById('avatars-picker-container');
const btnSaveProfile = document.getElementById('btn-save-profile');

// Floating Toast Notification System
function showToast(message, type = 'info') {
  if (!toastContainer) return;
  const toast = document.createElement('div');
  toast.className = `toast-msg ${type}`;
  toast.innerHTML = `<span>${type === 'success' ? '✅' : type === 'error' ? '⚠️' : 'ℹ️'}</span> <span>${message}</span>`;
  toastContainer.appendChild(toast);
  setTimeout(() => {
    toast.remove();
  }, 2600);
}

// 2. Systems Setup
const urlParams = new URLSearchParams(window.location.search);
const customServer = urlParams.get('server') || localStorage.getItem('wyhsiar_server_url');
const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const SERVER_URL = import.meta.env.VITE_SERVER_URL || customServer || (isLocalhost ? 'http://localhost:3005' : window.location.origin);

console.log(`🎮 WYHSIAR Games World initializing... Mode: ${isLocalhost ? 'Local Development' : 'Production Web'}`);

const socket = io(SERVER_URL, {
  transports: ['websocket', 'polling'],
  reconnection: true,
  reconnectionAttempts: 5,
  reconnectionDelay: 2000,
  autoConnect: true
});

socket.on('connect', () => {
  console.log(`✅ Connected to Game Server (Socket ID: ${socket.id})`);
});

socket.on('connect_error', () => {
  // Silent fallback to local in-browser simulation on static CDN
});

const avatarManager = new AvatarManager();
let currentRoom = null;
let selectedGameId = 'drawGuess';
let previewGameId = null;
let activeGameInstance = null;
let activeLocalBridge = null;
let currentCategoryFilter = 'all';

// Favorites Persistence
let favorites = JSON.parse(localStorage.getItem('wyhsiar_favs') || '[]');

// Chat Manager
const chatManager = new ChatManager(
  socket,
  document.getElementById('chat-messages-container'),
  document.getElementById('chat-input'),
  document.getElementById('btn-send-chat'),
  document.getElementById('reaction-bar')
);

// 3. Friv 20-Game Visual Catalog
const gameCatalog = [
  {
    id: 'drawGuess',
    title: 'Draw & Guess',
    category: 'party',
    categoryName: '🎉 Party & Social',
    icon: '🎨',
    tag: '2-8 Players',
    badge: 'POPULAR',
    controls: '🖱️ Mouse/Touch to Draw • Type Guesses in Chat',
    desc: 'Real-time drawing canvas! Choose secret words, sketch them out, and guess fast to score points.'
  },
  {
    id: 'ludoParty',
    title: 'Ludo Battle',
    category: 'card',
    categoryName: '🎴 Cards & Board',
    icon: '🎲',
    tag: '2-4 Players',
    badge: 'CLASSIC',
    controls: '🎲 Tap Roll Dice • Tap Highlighted Pawns to Move',
    desc: 'Authentic 15x15 Ludo board! Roll 6 to exit base, knock out opponent tokens, and race all 4 tokens Home.'
  },
  {
    id: 'poolGame',
    title: '8-Ball Pool',
    category: 'duel',
    categoryName: '⚔️ 2-Player Duels',
    icon: '🎱',
    tag: '2 Players',
    badge: 'BILLIARDS',
    controls: '🖱️ Aim Cue with Touch/Mouse • Power Slider • Tap Strike',
    desc: 'Realistic 2D billiards! Aim your cue, adjust shot power, sink your suit (Solids vs Stripes), and pocket the 8-Ball!'
  },
  {
    id: 'bombTag',
    title: 'Bomb Tag',
    category: 'party',
    categoryName: '🎉 Party & Social',
    icon: '💣',
    tag: '2-8 Players',
    badge: 'HOT POTATO',
    controls: '🕹️ On-Screen D-Pad or WASD/Arrows to Run & Tag',
    desc: 'Intense 30Hz tag arena! Pass the ticking bomb to friends before the fuse blows and you explode!'
  },
  {
    id: 'soccerGame',
    title: 'Head Soccer 2D',
    category: 'duel',
    categoryName: '⚔️ 2-Player Duels',
    icon: '⚽',
    tag: '2-4 Players',
    badge: 'SPORTS DUEL',
    controls: '🕹️ On-Screen Move, Jump & Kick Buttons or WASD+Space',
    desc: 'Fast 60Hz side-view soccer duel! Jump, header, and power-kick the bouncy ball into opponent goalposts.'
  },
  {
    id: 'battleship',
    title: 'Naval Battleship',
    category: 'card',
    categoryName: '🎴 Cards & Board',
    icon: '🚢',
    tag: '2 Players',
    badge: 'FLEET STRIKE',
    controls: '🖱️ Tap Enemy Radar Coordinates to Launch Missiles',
    desc: '10x10 tactical naval grid duel! Launch missile strikes, track sonar radar, and sink all 5 enemy warships.'
  },
  {
    id: 'airHockey',
    title: 'Cyber Air Hockey',
    category: 'duel',
    categoryName: '⚔️ 2-Player Duels',
    icon: '🏒',
    tag: '2 Players',
    badge: '60HZ GLOW',
    controls: '🖱️ Touch Drag / Mouse on your table side to strike puck',
    desc: 'High-speed neon air hockey table with smooth mallet physics, cushion rebounds, and race to 7 points!'
  },
  {
    id: 'archeryClash',
    title: 'Archery Clash',
    category: 'arcade',
    categoryName: '🕹️ Arcade & Action',
    icon: '🎯',
    tag: '2-8 Players',
    badge: 'TARGET MANIA',
    controls: '🖱️ Touch/Mouse Aim • Tension Slider • Release Arrow',
    desc: 'Precision bow shooting battle! Factor in variable crosswinds and hit the moving bullseye for 10 points.'
  },
  {
    id: 'kartRacers',
    title: 'Kart Racers',
    category: 'arcade',
    categoryName: '🕹️ Arcade & Action',
    icon: '🏎️',
    tag: '2-8 Players',
    badge: 'TOURNAMENT',
    controls: '🕹️ Touch Steer & Gas Buttons or WASD/Arrows',
    desc: 'Multi-track tournament with barriers & grass friction! Top drivers advance to next track.'
  },
  {
    id: 'snakeArena',
    title: 'Neon Snake.io',
    category: 'arcade',
    categoryName: '🕹️ Arcade & Action',
    icon: '🐍',
    tag: '2-8 Players',
    badge: 'REAL TIME',
    controls: '🖱️ Touch/Mouse Drag to Steer • ⚡ Tap Reborn on Death',
    desc: 'Electrified boundary arena with instant ⚡ REBORN! Eat energy orbs, grow massive, and trap friends.'
  },
  {
    id: 'tankArena',
    title: 'Tank 2D Battle',
    category: 'arcade',
    categoryName: '🕹️ Arcade & Action',
    icon: '💥',
    tag: '2-8 Players',
    badge: 'ACTION WAR',
    controls: '🕹️ Touch D-Pad to Drive + 💥 Fire Button or WASD+Mouse',
    desc: 'Top-down tank maze war! Steer your tank, shoot bouncing shells, destroy crates, and survive.'
  },
  {
    id: 'triviaMania',
    title: 'Trivia Quiz',
    category: 'word',
    categoryName: '💡 Word & Brain',
    icon: '🧠',
    tag: '2-8 Players',
    badge: 'QUIZ MANIC',
    controls: '🖱️ Tap Topic & Answer Options A/B/C/D',
    desc: 'Host chooses topic! 100+ non-guessable questions across Gaming, Movies, Science, and Food.'
  },
  {
    id: 'spyGame',
    title: "Who's The Spy?",
    category: 'party',
    categoryName: '🎉 Party & Social',
    icon: '🕵️',
    tag: '3-8 Players',
    badge: 'SOCIAL DEDUCTION',
    controls: '🔒 Tap Card to Reveal Word • Type 1-Sentence Clue • Vote',
    desc: 'Social deduction party game! Describe secret words, deduce who the undercover spy is, and vote them out.'
  },
  {
    id: 'wordBomb',
    title: 'Word Bomb',
    category: 'word',
    categoryName: '💡 Word & Brain',
    icon: '💣',
    tag: '2-8 Players',
    badge: 'FAST PACED',
    controls: '⌨️ Type Words Containing Prompt Letters & Tap Enter',
    desc: 'Fast-paced ticking bomb! Type valid dictionary words containing the prompt letters before you explode.'
  },
  {
    id: 'tetrisBattle',
    title: 'Block Clash',
    category: 'arcade',
    categoryName: '🕹️ Arcade & Action',
    icon: '🧱',
    tag: '2-4 Players',
    badge: 'PUZZLE DUEL',
    controls: '🕹️ Touch ◀ ▶ ▼ 🔄 ⚡ Buttons or Keyboard Arrow Keys',
    desc: 'Falling tetrominoes battle! Clear multiple lines to send solid garbage rows to your opponents.'
  },
  {
    id: 'spaceSurvival',
    title: 'Space Survival',
    category: 'arcade',
    categoryName: '🕹️ Arcade & Action',
    icon: '🚀',
    tag: '2-8 Players',
    badge: 'SURVIVAL',
    controls: '🕹️ Touch ◀ ▶ ▲ ▼ Buttons or WASD/Arrows to Dodge',
    desc: 'Spectator mode on death & live survival leaderboard! Dodge cosmic meteor storms.'
  },
  {
    id: 'ticTacToe',
    title: 'Glow Tic-Tac-Toe',
    category: 'duel',
    categoryName: '⚔️ 2-Player Duels',
    icon: '⭕',
    tag: '2 Players Duel',
    badge: 'QUICK DUEL',
    controls: '🖱️ Tap Empty 3x3 Grid Cells to Place Mark',
    desc: 'Fast neon grid duel with glowing line strokes, instant replay, and spectator cheering.'
  },
  {
    id: 'connect4',
    title: 'Connect 4',
    category: 'duel',
    categoryName: '⚔️ 2-Player Duels',
    icon: '🔴',
    tag: '2 Players Duel',
    badge: 'STRATEGY',
    controls: '🖱️ Tap Board Column (1-7) to Drop Chip',
    desc: 'Classic 4-in-a-row chip drop battle! Drop your chips strategically and cheer as spectators.'
  },
  {
    id: 'cyberPong',
    title: 'Cyber Pong',
    category: 'duel',
    categoryName: '⚔️ 2-Player Duels',
    icon: '🏓',
    tag: '2 Players Duel',
    badge: 'AIR HOCKEY',
    controls: '🖱️ Drag Touch / Mouse Up & Down to Control Paddle',
    desc: 'Ultra-fast neon air hockey / pong duel! Smooth paddle control and electric ball trails.'
  },
  {
    id: 'unoCard',
    title: 'Uno Party Cards',
    category: 'card',
    categoryName: '🎴 Cards & Board',
    icon: '🃏',
    tag: '2-8 Players',
    badge: 'CARD BATTLE',
    controls: '🖱️ Tap Matching Color/Number Card • Tap Draw Deck',
    desc: 'Classic color & number card battle! Play +4 Wild, Skip, and Reverse cards and call UNO.'
  }
];

// 4. Render Friv Dense Visual Mosaic Grid
function renderGameGrid() {
  if (!gameGridContainer) return;
  gameGridContainer.innerHTML = '';
  const searchVal = inputGameSearch ? inputGameSearch.value.trim().toLowerCase() : '';

  let filtered = gameCatalog;

  // Category Filter
  if (currentCategoryFilter === 'favorites') {
    filtered = filtered.filter(g => favorites.includes(g.id));
  } else if (currentCategoryFilter !== 'all') {
    filtered = filtered.filter(g => g.category === currentCategoryFilter);
  }

  // Search Filter
  if (searchVal) {
    filtered = filtered.filter(g =>
      g.title.toLowerCase().includes(searchVal) ||
      g.categoryName.toLowerCase().includes(searchVal) ||
      g.desc.toLowerCase().includes(searchVal)
    );
  }

  if (filtered.length === 0) {
    gameGridContainer.innerHTML = `
      <div style="grid-column: 1 / -1; text-align:center; padding: 60px 20px; color:#94a3b8;">
        <span style="font-size:3rem; display:block; margin-bottom:12px;">🔍</span>
        <h3>No games matched "${searchVal || currentCategoryFilter}"</h3>
        <p style="margin-top:6px;">Try searching for another title or clear the filter.</p>
      </div>
    `;
    return;
  }

  filtered.forEach((game) => {
    const isFav = favorites.includes(game.id);
    const tile = document.createElement('div');
    tile.className = 'friv-tile';
    tile.innerHTML = `
      <div class="tile-top-row">
        <span class="tile-badge">${game.badge}</span>
        <button class="btn-star-fav ${isFav ? 'active' : ''}" title="Favorite">★</button>
      </div>

      <div class="tile-artwork">
        <span class="tile-artwork-icon">${game.icon}</span>
      </div>

      <div class="tile-bottom-info">
        <div class="tile-title">${game.title}</div>
        <div class="tile-meta">${game.tag}</div>
      </div>
    `;

    // Star Favorite Toggle
    const starBtn = tile.querySelector('.btn-star-fav');
    starBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      sounds.playPop();
      if (favorites.includes(game.id)) {
        favorites = favorites.filter(id => id !== game.id);
      } else {
        favorites.push(game.id);
      }
      localStorage.setItem('wyhsiar_favs', JSON.stringify(favorites));
      renderGameGrid();
    });

    // Tile Hover Sound & Pop
    tile.addEventListener('mouseenter', () => {
      sounds.playHover();
    });

    // Tile Click -> Open Friv Spotlight Modal
    tile.addEventListener('click', () => {
      sounds.playPop();
      openGamePreview(game);
    });

    gameGridContainer.appendChild(tile);
  });
}

// Dynamic Player Capacity Generator Based on Game Category
function getPlayerOptionsForGame(gameId) {
  const game = gameCatalog.find(g => g.id === gameId);
  if (!game) return [{ value: 4, label: '4 Players' }];

  if (game.category === 'duel' || ['ticTacToe', 'connect4', 'cyberPong', 'battleship', 'airHockey', 'archeryClash'].includes(gameId)) {
    return [{ value: 2, label: '2 Players (Duel Locked ⚔️)' }];
  }

  if (gameId === 'ludoParty') {
    return [
      { value: 2, label: '2 Players (Duel)' },
      { value: 3, label: '3 Players' },
      { value: 4, label: '4 Players (Classic)' },
      { value: 5, label: '5 Players (Star Board)' },
      { value: 6, label: '6 Players (Star Board)' }
    ];
  }

  if (gameId === 'tetrisBattle') {
    return [
      { value: 2, label: '2 Players (Duel)' },
      { value: 3, label: '3 Players' },
      { value: 4, label: '4 Players' }
    ];
  }

  if (game.category === 'party' || game.category === 'word' || ['drawGuess', 'triviaMania', 'wordBomb', 'spyGame', 'bombTag', 'spaceSurvival'].includes(gameId)) {
    return [
      { value: 4, label: '4 Players' },
      { value: 8, label: '8 Players' },
      { value: 12, label: '12 Players (Party 🎉)' },
      { value: 16, label: '16 Players (Mega Party)' },
      { value: 20, label: '20 Players (Max Capacity 👑)' }
    ];
  }

  // Cards, Arcade, Sports default:
  return [
    { value: 2, label: '2 Players' },
    { value: 3, label: '3 Players' },
    { value: 4, label: '4 Players (Default)' },
    { value: 6, label: '6 Players' },
    { value: 8, label: '8 Players' }
  ];
}

function updateRoomPlayerSelectOptions(gameId) {
  if (!selectRoomPlayers) return;
  const currentVal = parseInt(selectRoomPlayers.value) || 4;
  const opts = getPlayerOptionsForGame(gameId);
  selectRoomPlayers.innerHTML = '';
  let foundMatch = false;
  opts.forEach(opt => {
    const el = document.createElement('option');
    el.value = opt.value;
    el.innerText = opt.label;
    if (opt.value === currentVal) {
      el.selected = true;
      foundMatch = true;
    }
    selectRoomPlayers.appendChild(el);
  });
  if (!foundMatch && selectRoomPlayers.options.length > 0) {
    selectRoomPlayers.selectedIndex = selectRoomPlayers.options.length > 2 ? 2 : 0;
  }
}

// Open Friv Spotlight Preview Modal
function openGamePreview(game) {
  if (!game) return;
  previewGameId = game.id;
  selectedGameId = game.id;

  if (previewIcon) previewIcon.innerText = game.icon;
  if (previewBadge) previewBadge.innerText = game.badge;
  if (previewTitle) previewTitle.innerText = game.title;
  if (previewCategory) previewCategory.innerText = game.categoryName;
  if (previewPlayers) previewPlayers.innerText = `👥 ${game.tag}`;
  if (previewDesc) previewDesc.innerText = game.desc;
  if (previewControls) previewControls.innerText = game.controls;

  // Dynamically populate player options for this specific game
  const opts = getPlayerOptionsForGame(game.id);
  if (selectModalPlayers) {
    selectModalPlayers.innerHTML = '';
    opts.forEach(opt => {
      const el = document.createElement('option');
      el.value = opt.value;
      el.innerText = opt.label;
      if (opt.value === 4 || (opts.length === 1 && opt.value === opts[0].value)) {
        el.selected = true;
      }
      selectModalPlayers.appendChild(el);
    });
  }

  if (gamePreviewModal) {
    gamePreviewModal.classList.remove('modal-hidden');
  }
}

function closeGamePreview() {
  if (gamePreviewModal) {
    gamePreviewModal.classList.add('modal-hidden');
  }
}

btnClosePreview?.addEventListener('click', closeGamePreview);
previewBackdrop?.addEventListener('click', closeGamePreview);

// Unified Game Client Launcher (Supports both Server Socket and in-browser Local Bridge)
function launchGameClient(gameId, activeSocket) {
  const gameDef = gameCatalog.find(g => g.id === gameId);
  if (activeGameTitle) activeGameTitle.innerText = gameDef ? gameDef.title.toUpperCase() : 'PARTY GAME';
  if (activeGameIcon) activeGameIcon.innerText = gameDef ? gameDef.icon : '🎮';
  if (activeGameControlsHint) activeGameControlsHint.innerText = gameDef ? `Controls: ${gameDef.controls}` : 'Controls: Mouse & Touch';

  if (activeGameInstance?.destroy) {
    activeGameInstance.destroy();
  }
  if (activeGameContainer) activeGameContainer.innerHTML = '';

  // 20 Games Factory
  if (gameId === 'drawGuess') activeGameInstance = new DrawGuessClient(activeSocket, activeGameContainer);
  else if (gameId === 'ludoParty') activeGameInstance = new LudoGameClient(activeSocket, activeGameContainer);
  else if (gameId === 'poolGame') activeGameInstance = new PoolGameClient(activeSocket, activeGameContainer);
  else if (gameId === 'soccerGame') activeGameInstance = new SoccerGameClient(activeSocket, activeGameContainer);
  else if (gameId === 'bombTag') activeGameInstance = new BombTagGameClient(activeSocket, activeGameContainer);
  else if (gameId === 'battleship') activeGameInstance = new BattleshipGameClient(activeSocket, activeGameContainer);
  else if (gameId === 'airHockey') activeGameInstance = new AirHockeyGameClient(activeSocket, activeGameContainer);
  else if (gameId === 'archeryClash') activeGameInstance = new ArcheryGameClient(activeSocket, activeGameContainer);
  else if (gameId === 'kartRacers') activeGameInstance = new KartGameClient(activeSocket, activeGameContainer);
  else if (gameId === 'snakeArena') activeGameInstance = new SnakeArenaClient(activeSocket, activeGameContainer);
  else if (gameId === 'tankArena') activeGameInstance = new TankGameClient(activeSocket, activeGameContainer);
  else if (gameId === 'triviaMania') activeGameInstance = new TriviaGameClient(activeSocket, activeGameContainer);
  else if (gameId === 'spyGame') activeGameInstance = new SpyGameClient(activeSocket, activeGameContainer);
  else if (gameId === 'wordBomb') activeGameInstance = new WordBombClient(activeSocket, activeGameContainer);
  else if (gameId === 'tetrisBattle') activeGameInstance = new TetrisGameClient(activeSocket, activeGameContainer);
  else if (gameId === 'spaceSurvival') activeGameInstance = new SpaceSurvivalClient(activeSocket, activeGameContainer);
  else if (gameId === 'ticTacToe') activeGameInstance = new TicTacToeClient(activeSocket, activeGameContainer);
  else if (gameId === 'connect4') activeGameInstance = new Connect4Client(activeSocket, activeGameContainer);
  else if (gameId === 'cyberPong') activeGameInstance = new CyberPongClient(activeSocket, activeGameContainer);
  else if (gameId === 'unoCard') activeGameInstance = new UnoCardClient(activeSocket, activeGameContainer);

  // Universal Game Over Listeners across all 20 mini-games
  const gameOverEvents = [
    'dg_gameOver', 'pool_gameOver', 'soccer_gameOver', 'bt_gameOver',
    'bs_gameOver', 'hockey_gameOver', 'archery_gameOver', 'kart_gameOver', 'snake_gameOver',
    'tank_gameOver', 'trivia_gameOver', 'spy_gameOver', 'wb_gameOver', 'tetris_gameOver',
    'space_gameOver', 'ttt_gameOver', 'c4_gameOver', 'pong_gameOver', 'uno_gameOver'
  ];

  gameOverEvents.forEach((evt) => {
    activeSocket.on(evt, (data) => {
      setTimeout(() => {
        if (!document.querySelector('.ludo-leaderboard-modal') && !document.querySelector('.universal-gameover-modal')) {
          const winnerName = data?.winner?.name || data?.winner || (data?.leaderboard?.[0]?.name) || '';
          showUniversalGameOverModal({
            title: '🏆 MATCH FINISHED!',
            winner: winnerName ? { name: winnerName } : null,
            message: data?.message || 'Great match! Choose an action below.'
          });
        }
      }, 300);
    });
  });

  showView('game');
  sounds.playChime();
}

// =========================================================
// UNIVERSAL IN-GAME GAME OVER / LEADERBOARD & RETURN MODAL
// =========================================================
function showUniversalGameOverModal({ title = 'MATCH COMPLETE 🏆', message = '', winner = null } = {}) {
  const existing = document.querySelector('.universal-gameover-modal');
  if (existing) existing.remove();

  sounds.playVictory();

  const isRoom = !!(currentRoom || activeLocalBridge?.room);
  const modal = document.createElement('div');
  modal.className = 'universal-gameover-modal';
  modal.innerHTML = `
    <div class="universal-gameover-card">
      <h2>${title}</h2>
      <div class="universal-gameover-msg">
        ${winner ? `<p style="font-size:1.15rem; font-weight:800; color:#38bdf8; margin:8px 0;">🎉 Champion: ${winner.name || winner} 🎉</p>` : ''}
        <p>${message}</p>
      </div>

      <div class="universal-gameover-actions">
        ${isRoom ? `
          <button id="btn-uni-lobby" class="btn-lb-primary">🏠 RETURN TO ROOM LOBBY</button>
          <button id="btn-uni-hub" class="btn-lb-secondary">🌐 EXIT TO MAIN HUB</button>
        ` : `
          <button id="btn-uni-hub" class="btn-lb-primary">🌐 RETURN TO MAIN HUB</button>
          <button id="btn-uni-retry" class="btn-lb-secondary">🔄 PLAY AGAIN</button>
        `}
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  modal.querySelector('#btn-uni-lobby')?.addEventListener('click', () => {
    sounds.playPop();
    modal.remove();
    if (activeLocalBridge) activeLocalBridge.emit('returnToLobby');
    else if (currentRoom) socket.emit('returnToLobby');
    else showView('hub');
  });

  modal.querySelector('#btn-uni-hub')?.addEventListener('click', () => {
    sounds.playPop();
    modal.remove();
    if (activeGameInstance?.destroy) {
      activeGameInstance.destroy();
      activeGameInstance = null;
    }
    if (activeLocalBridge?.destroy) {
      activeLocalBridge.destroy();
      activeLocalBridge = null;
    }
    currentRoom = null;
    showView('hub');
    renderGameGrid();
  });

  modal.querySelector('#btn-uni-retry')?.addEventListener('click', () => {
    sounds.playPop();
    modal.remove();
    if (selectedGameId) {
      launchGameClient(selectedGameId, activeLocalBridge || socket);
    }
  });
}

window.showGlobalGameOverModal = showUniversalGameOverModal;

// Safe in-game alert override to prevent intrusive browser popups
const _nativeAlert = window.alert;
window.alert = function(msg) {
  const gameView = document.getElementById('view-game');
  if (gameView && !gameView.classList.contains('view-hidden')) {
    showUniversalGameOverModal({
      title: '🏆 GAME NOTICE',
      message: msg
    });
    return;
  }
  _nativeAlert(msg);
};

// Direct Actions in Preview Modal
btnPreviewInstantPlay?.addEventListener('click', () => {
  sounds.playSuccess();
  closeGamePreview();

  const targetPlayers = parseInt(selectModalPlayers?.value || '4');
  const fillBots = checkModalBots?.checked !== false;

  console.log(`⚡ Instant Play triggered for: ${previewGameId} (Target: ${targetPlayers}, Bots: ${fillBots})`);

  if (socket.connected) {
    createPartyRoomAndLaunch(previewGameId, { targetPlayers, fillBots });
  } else {
    // Zero-latency in-browser local engine simulation (works on static Cloudflare Pages!)
    if (activeLocalBridge?.destroy) activeLocalBridge.destroy();
    const prof = avatarManager.getProfile();
    activeLocalBridge = new LocalSocketBridge(prof.name, prof.avatar);
    activeLocalBridge.startLocalGame(previewGameId, { targetPlayers, fillBots });
    launchGameClient(previewGameId, activeLocalBridge);
  }
});

btnPreviewPartyRoom?.addEventListener('click', () => {
  sounds.playChime();
  closeGamePreview();
  createPartyRoom(previewGameId);
});

// Render Host Mini Selector in Room
function renderRoomGameSelector() {
  if (!selectorCardsRow) return;
  selectorCardsRow.innerHTML = '';
  gameCatalog.forEach((game) => {
    const mini = document.createElement('div');
    mini.className = `mini-game-card ${game.id === selectedGameId ? 'selected' : ''}`;
    mini.innerHTML = `
      <div class="mini-icon">${game.icon}</div>
      <div class="mini-title">${game.title}</div>
    `;

    mini.addEventListener('click', () => {
      selectorCardsRow.querySelectorAll('.mini-game-card').forEach(m => m.classList.remove('selected'));
      mini.classList.add('selected');
      selectedGameId = game.id;
      updateRoomPlayerSelectOptions(game.id);
      sounds.playPop();
    });

    selectorCardsRow.appendChild(mini);
  });
}

// Render Virtual Seats in Room (Dynamic scaling from 2 to 20+ Players)
function renderSeats(players = []) {
  if (!seatsContainer) return;
  seatsContainer.innerHTML = '';

  const myId = activeLocalBridge ? activeLocalBridge.id : socket.id;
  const isHost = currentRoom && (currentRoom.hostId === myId);
  const targetCount = currentRoom?.settings?.targetPlayers || Math.max(players.length, 4);
  const totalSeats = Math.max(targetCount, players.length);

  if (roomCountBadge) {
    roomCountBadge.innerText = `👥 ${players.length} / ${targetCount} PLAYERS`;
  }

  for (let i = 0; i < totalSeats; i++) {
    const p = players[i];
    const seat = document.createElement('div');
    seat.className = `seat-card ${p ? 'occupied' : 'empty'} ${p?.isHost ? 'host' : ''}`;

    if (p) {
      seat.innerHTML = `
        ${isHost && !p.isHost ? `<button class="btn-kick-seat" data-id="${p.id}" title="Remove / Kick">✕</button>` : ''}
        <div class="seat-avatar">${p.avatar || '👤'}</div>
        <div class="seat-name">
          <span>${p.name}</span>
          ${p.isBot ? '<span class="bot-tag">BOT</span>' : ''}
        </div>
        <div class="seat-status ${p.isHost ? 'host' : (p.isReady ? 'ready' : '')}">
          ${p.isHost ? '👑 HOST' : (p.isReady ? 'READY ✅' : 'WAITING...')}
        </div>
      `;

      const kickBtn = seat.querySelector('.btn-kick-seat');
      kickBtn?.addEventListener('click', (e) => {
        e.stopPropagation();
        sounds.playPop();
        const targetId = kickBtn.dataset.id;
        if (activeLocalBridge) activeLocalBridge.emit('removePlayer', { targetId });
        else socket.emit('removePlayer', { targetId });
      });
    } else {
      seat.innerHTML = `
        <div class="seat-avatar" style="opacity:0.3;">🪑</div>
        <div class="seat-name" style="color:#64748b;">Seat ${i + 1}</div>
        ${isHost ? `<button class="btn-add-bot-seat" title="Add Bot into Seat ${i + 1}">🤖 + Add Bot</button>` : '<div class="seat-status">OPEN</div>'}
      `;

      const addBotBtn = seat.querySelector('.btn-add-bot-seat');
      addBotBtn?.addEventListener('click', (e) => {
        e.stopPropagation();
        sounds.playPop();
        if (activeLocalBridge) activeLocalBridge.emit('addBot');
        else socket.emit('addBot');
      });
    }
    seatsContainer.appendChild(seat);
  }
}

// 5. Navigation & View Router
function showView(viewName) {
  viewHub?.classList.add('view-hidden');
  viewRoom?.classList.add('view-hidden');
  viewGame?.classList.add('view-hidden');

  if (viewName === 'hub') viewHub?.classList.remove('view-hidden');
  if (viewName === 'room') viewRoom?.classList.remove('view-hidden');
  if (viewName === 'game') viewGame?.classList.remove('view-hidden');
}

// 6. User Profile Update
function updateProfileUI() {
  const prof = avatarManager.getProfile();
  if (hubAvatar) hubAvatar.innerText = prof.avatar;
  if (hubName) hubName.innerText = prof.name;
}
updateProfileUI();

// 7. Room Creation & Quick Launch
let pendingLaunchGameId = null;
let pendingRoomSettings = null;

async function createPartyRoom(initialGameId = 'drawGuess') {
  const prof = avatarManager.getProfile();
  if (socket.connected) {
    socket.emit('createRoom', { name: prof.name, avatar: prof.avatar });
  } else {
    // Multi-Device P2P WebRTC Room Host
    if (activeLocalBridge?.destroy) activeLocalBridge.destroy();
    activeLocalBridge = new P2PNetworkBridge(prof.name, prof.avatar);
    chatManager.updateSocket(activeLocalBridge);

    activeLocalBridge.on('roomJoined', ({ room, isHost }) => {
      currentRoom = room;
      selectedGameId = initialGameId || 'drawGuess';
      if (roomDisplayCode) roomDisplayCode.innerText = room.code;
      const targetCount = room.settings?.targetPlayers || 4;
      if (roomCountBadge) roomCountBadge.innerText = `👥 ${room.players.length} / ${targetCount} PLAYERS`;
      renderSeats(room.players);
      renderRoomGameSelector();
      updateRoomPlayerSelectOptions(selectedGameId);
      showView('room');

      if (btnStartSelectedGame) btnStartSelectedGame.style.display = 'block';
      if (btnReadyToggle) btnReadyToggle.style.display = 'none';
      if (hostSettingsPanel) hostSettingsPanel.style.display = 'flex';
      if (hostNotice) hostNotice.innerText = 'Party Lounge 👑: Share your code to let friends join, or add bots!';
      sounds.playVictory();
    });

    activeLocalBridge.on('roomUpdated', ({ room }) => {
      currentRoom = room;
      const targetCount = room.settings?.targetPlayers || 4;
      if (roomCountBadge) roomCountBadge.innerText = `👥 ${room.players.length} / ${targetCount} PLAYERS`;
      renderSeats(room.players);
      if (room.settings) {
        if (selectRoomPlayers) selectRoomPlayers.value = `${room.settings.targetPlayers || 4}`;
        if (selectRoomBots) selectRoomBots.value = `${room.settings.fillBots !== false}`;
        if (selectTurnTime) selectTurnTime.value = `${room.settings.roundTimer}`;
        if (selectTotalRounds) selectTotalRounds.value = `${room.settings.totalRounds}`;
      }
    });

    activeLocalBridge.on('hostMigrated', ({ newHostId, newHost, room }) => {
      currentRoom = room;
      const myId = activeLocalBridge.id;
      const hostId = newHostId || newHost?.id;
      const isHost = hostId === myId;
      if (isHost) {
        showToast('👑 You are now the Room Host!', 'success');
        sounds.playVictory();
        if (btnStartSelectedGame) btnStartSelectedGame.style.display = 'block';
        if (btnReadyToggle) btnReadyToggle.style.display = 'none';
        if (hostSettingsPanel) hostSettingsPanel.style.display = 'flex';
      }
      renderSeats(room.players);
    });

    activeLocalBridge.on('chatMessage', (msg) => {
      chatManager.appendMessage(msg);
      if (roomLayout?.classList.contains('show-stage') && mobileChatBadge) {
        mobileChatBadge.classList.remove('modal-hidden');
      }
    });

    activeLocalBridge.on('reactionGift', (data) => {
      chatManager.triggerLocalReaction(data.emoji);
    });

    activeLocalBridge.on('gameLaunched', ({ gameId }) => {
      launchGameClient(gameId, activeLocalBridge);
    });

    activeLocalBridge.on('returnedToLobby', ({ room }) => {
      currentRoom = room;
      if (activeGameInstance?.destroy) {
        activeGameInstance.destroy();
        activeGameInstance = null;
      }
      showView('room');
      renderSeats(room.players);
      sounds.playChime();
    });

    await activeLocalBridge.createRoom(initialGameId);
  }
}

function createPartyRoomAndLaunch(gameId, customSettings = null) {
  pendingLaunchGameId = gameId;
  pendingRoomSettings = customSettings;
  const prof = avatarManager.getProfile();
  socket.emit('createRoom', { name: prof.name, avatar: prof.avatar });
}

async function joinPartyRoom(rawCode) {
  let code = (rawCode || '').toUpperCase().trim();
  if (!code) {
    alert('Please enter a valid Party Room Code!');
    return;
  }
  if (!code.startsWith('WP-') && code.length === 4) {
    code = `WP-${code}`;
  }

  const prof = avatarManager.getProfile();
  if (socket.connected) {
    socket.emit('joinRoom', { code, name: prof.name, avatar: prof.avatar });
  } else {
    // Multi-Device P2P WebRTC Guest Join
    if (activeLocalBridge?.destroy) activeLocalBridge.destroy();
    activeLocalBridge = new P2PNetworkBridge(prof.name, prof.avatar);
    chatManager.updateSocket(activeLocalBridge);

    activeLocalBridge.on('roomJoined', ({ room, isHost }) => {
      currentRoom = room;
      selectedGameId = room.currentGame || 'drawGuess';
      if (roomDisplayCode) roomDisplayCode.innerText = room.code;
      const targetCount = room.settings?.targetPlayers || 4;
      if (roomCountBadge) roomCountBadge.innerText = `👥 ${room.players.length} / ${targetCount} PLAYERS`;
      renderSeats(room.players);
      renderRoomGameSelector();
      updateRoomPlayerSelectOptions(selectedGameId);
      showView('room');

      if (btnStartSelectedGame) btnStartSelectedGame.style.display = 'none';
      if (btnReadyToggle) btnReadyToggle.style.display = 'block';
      if (hostSettingsPanel) hostSettingsPanel.style.display = 'none';
      if (hostNotice) hostNotice.innerText = 'Joined Room 🎉 Waiting for Host 👑 to launch game...';
      sounds.playVictory();
    });

    activeLocalBridge.on('roomUpdated', ({ room }) => {
      currentRoom = room;
      const targetCount = room.settings?.targetPlayers || 4;
      if (roomCountBadge) roomCountBadge.innerText = `👥 ${room.players.length} / ${targetCount} PLAYERS`;
      renderSeats(room.players);
      if (room.settings) {
        if (selectRoomPlayers) selectRoomPlayers.value = `${room.settings.targetPlayers || 4}`;
        if (selectRoomBots) selectRoomBots.value = `${room.settings.fillBots !== false}`;
        if (selectTurnTime) selectTurnTime.value = `${room.settings.roundTimer}`;
        if (selectTotalRounds) selectTotalRounds.value = `${room.settings.totalRounds}`;
      }
    });

    activeLocalBridge.on('hostMigrated', ({ newHostId, newHost, room }) => {
      currentRoom = room;
      const myId = activeLocalBridge.id;
      const hostId = newHostId || newHost?.id;
      const isHost = hostId === myId;
      if (isHost) {
        showToast('👑 You are now the Room Host!', 'success');
        sounds.playVictory();
        if (btnStartSelectedGame) btnStartSelectedGame.style.display = 'block';
        if (btnReadyToggle) btnReadyToggle.style.display = 'none';
        if (hostSettingsPanel) hostSettingsPanel.style.display = 'flex';
        if (hostNotice) hostNotice.innerText = 'You are now Room Host 👑!';
      } else {
        showToast('👑 Room host migrated to next player.', 'info');
      }
      renderSeats(room.players);
    });

    activeLocalBridge.on('joinError', ({ message }) => {
      showToast(`⚠️ ${message}`, 'error');
    });

    activeLocalBridge.on('kickedFromRoom', ({ message }) => {
      showToast(`⚠️ ${message || 'You were removed from the room by the Host.'}`, 'error');
      if (activeLocalBridge) {
        activeLocalBridge.destroy();
        activeLocalBridge = null;
      }
      showView('hub');
      renderGameGrid();
    });

    activeLocalBridge.on('chatMessage', (msg) => {
      chatManager.appendMessage(msg);
      if (roomLayout?.classList.contains('show-stage') && mobileChatBadge) {
        mobileChatBadge.classList.remove('modal-hidden');
      }
    });

    activeLocalBridge.on('reactionGift', (data) => {
      chatManager.triggerLocalReaction(data.emoji);
    });

    activeLocalBridge.on('gameLaunched', ({ gameId }) => {
      launchGameClient(gameId, activeLocalBridge);
    });

    activeLocalBridge.on('returnedToLobby', ({ room }) => {
      currentRoom = room;
      if (activeGameInstance?.destroy) {
        activeGameInstance.destroy();
        activeGameInstance = null;
      }
      showView('room');
      renderSeats(room.players);
      sounds.playChime();
    });

    try {
      await activeLocalBridge.joinRoom(code);
    } catch (e) {
      console.error('Join error:', e);
    }
  }
}

btnCreateRoom?.addEventListener('click', () => {
  createPartyRoom();
});

btnJoinRoom?.addEventListener('click', () => {
  const code = inputJoinCode ? inputJoinCode.value.trim() : '';
  if (code) joinPartyRoom(code);
});

inputJoinCode?.addEventListener('keydown', (e) => {
  if (e.key === 'Enter') {
    const code = inputJoinCode.value.trim();
    if (code) joinPartyRoom(code);
  }
});

btnLeaveRoom?.addEventListener('click', () => {
  if (activeLocalBridge) {
    activeLocalBridge.destroy();
    activeLocalBridge = null;
  }
  showView('hub');
  renderGameGrid();
});

// 1-Click Viral Invite Deep Links & WhatsApp Sharing
async function copyRoomInviteLink() {
  const code = currentRoom?.code || (activeLocalBridge?.room?.code);
  if (!code) {
    showToast('No active room found to share', 'error');
    return;
  }
  const link = `${window.location.origin}/?room=${code}`;
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(link);
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = link;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      textArea.remove();
    }
    showToast('📋 Room invite link copied to clipboard!', 'success');
    sounds.playChime();
    if (btnCopyRoomLink) {
      btnCopyRoomLink.innerText = 'COPIED! ✅';
      setTimeout(() => { btnCopyRoomLink.innerText = '📋 COPY INVITE LINK'; }, 1800);
    }
    if (btnInGameInvite) {
      btnInGameInvite.innerText = 'COPIED! ✅';
      setTimeout(() => { btnInGameInvite.innerText = '📋 INVITE'; }, 1800);
    }
  } catch (err) {
    console.error('Failed to copy link:', err);
    showToast(`Invite link: ${link}`);
  }
}

function shareRoomToWhatsApp() {
  const code = currentRoom?.code || (activeLocalBridge?.room?.code);
  if (!code) {
    showToast('No active room found to share', 'error');
    return;
  }
  const link = `${window.location.origin}/?room=${code}`;
  const text = `Join my online multiplayer party game on WYHSIAR GAMES WORLD! 🎮✨\nRoom Code: ${code}\nClick to join instantly: ${link}`;
  const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`;
  window.open(url, '_blank');
  sounds.playPop();
  showToast('💬 Opening WhatsApp to share room invite...', 'success');
}

btnCopyRoomLink?.addEventListener('click', copyRoomInviteLink);
btnWhatsappRoomLink?.addEventListener('click', shareRoomToWhatsApp);
btnInGameInvite?.addEventListener('click', copyRoomInviteLink);
btnInGameWhatsapp?.addEventListener('click', shareRoomToWhatsApp);

// Global Quick Play Matchmaking Controller
let quickPlayInterval = null;
let quickPlaySeconds = 5;
let isQuickPlayActive = false;

function startQuickPlay() {
  if (isQuickPlayActive) return;
  isQuickPlayActive = true;
  quickPlaySeconds = 5;

  if (quickplayCountdown) quickplayCountdown.innerText = '5';
  if (quickplayStatusText) quickplayStatusText.innerText = 'Searching for open party room...';
  if (quickplayModal) quickplayModal.classList.remove('modal-hidden');
  sounds.playPop();

  const prof = avatarManager.getProfile();

  if (socket.connected) {
    socket.emit('quickPlayMatchmake', { name: prof.name, avatar: prof.avatar });
  }

  clearInterval(quickPlayInterval);
  quickPlayInterval = setInterval(() => {
    quickPlaySeconds--;
    if (quickplayCountdown) quickplayCountdown.innerText = `${quickPlaySeconds}`;

    if (quickPlaySeconds <= 2 && quickplayStatusText) {
      quickplayStatusText.innerText = 'Almost there! Preparing match...';
    }

    if (quickPlaySeconds <= 0) {
      clearInterval(quickPlayInterval);
      quickPlayInterval = null;
      cancelQuickPlay(false);

      showToast('🚀 Launching Quick Play with AI Bots!', 'success');
      sounds.playVictory();

      // Popular party games for instant quick play
      const quickGames = ['triviaMania', 'drawGuess', 'unoCard', 'wordBomb', 'ludoParty'];
      const chosenGame = quickGames[Math.floor(Math.random() * quickGames.length)];

      if (socket.connected) {
        createPartyRoomAndLaunch(chosenGame, { targetPlayers: 4, fillBots: true });
      } else {
        if (activeLocalBridge?.destroy) activeLocalBridge.destroy();
        activeLocalBridge = new LocalSocketBridge(prof.name, prof.avatar);
        activeLocalBridge.startLocalGame(chosenGame, { targetPlayers: 4, fillBots: true });
        launchGameClient(chosenGame, activeLocalBridge);
      }
    }
  }, 1000);
}

function cancelQuickPlay(userInitiated = true) {
  if (quickPlayInterval) {
    clearInterval(quickPlayInterval);
    quickPlayInterval = null;
  }
  isQuickPlayActive = false;
  if (quickplayModal) quickplayModal.classList.add('modal-hidden');

  if (userInitiated) {
    showToast('Matchmaking cancelled');
    sounds.playPop();
  }
}

btnQuickPlay?.addEventListener('click', startQuickPlay);
btnCancelQuickplay?.addEventListener('click', () => cancelQuickPlay(true));
quickplayBackdrop?.addEventListener('click', () => cancelQuickPlay(true));

// Mobile View Navigation Tabs (< 768px)
const roomTabBtns = document.querySelectorAll('.room-tab-btn');
roomTabBtns.forEach(btn => {
  btn.addEventListener('click', () => {
    roomTabBtns.forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    sounds.playPop();

    const tab = btn.dataset.tab;
    if (tab === 'chat') {
      roomLayout?.classList.add('show-chat');
      roomLayout?.classList.remove('show-stage');
      if (mobileChatBadge) mobileChatBadge.classList.add('modal-hidden');
    } else {
      roomLayout?.classList.add('show-stage');
      roomLayout?.classList.remove('show-chat');
    }
  });
});

// Sound Toggle
btnSoundToggle?.addEventListener('click', () => {
  const isMuted = sounds.toggleMute();
  if (soundIcon) soundIcon.innerText = isMuted ? '🔇' : '🔊';
  if (btnGameSoundToggle) btnGameSoundToggle.innerText = isMuted ? '🔇' : '🔊';
});

btnGameSoundToggle?.addEventListener('click', () => {
  const isMuted = sounds.toggleMute();
  if (soundIcon) soundIcon.innerText = isMuted ? '🔇' : '🔊';
  if (btnGameSoundToggle) btnGameSoundToggle.innerText = isMuted ? '🔇' : '🔊';
});

// Fullscreen Toggle
function toggleFullscreen() {
  if (!document.fullscreenElement) {
    document.documentElement.requestFullscreen().catch(() => {});
  } else {
    document.exitFullscreen().catch(() => {});
  }
}
btnFullscreenToggle?.addEventListener('click', toggleFullscreen);
btnGameFullscreen?.addEventListener('click', toggleFullscreen);

// Search Bar Input
inputGameSearch?.addEventListener('input', () => {
  const val = inputGameSearch.value.trim();
  btnClearSearch?.classList.toggle('modal-hidden', !val);
  renderGameGrid();
});

btnClearSearch?.addEventListener('click', () => {
  if (inputGameSearch) inputGameSearch.value = '';
  btnClearSearch.classList.add('modal-hidden');
  renderGameGrid();
});

// Host settings listeners
selectRoomPlayers?.addEventListener('change', (e) => {
  const settings = { targetPlayers: parseInt(e.target.value) };
  if (activeLocalBridge) activeLocalBridge.emit('updateRoomSettings', settings);
  else socket.emit('updateRoomSettings', settings);
});

selectRoomBots?.addEventListener('change', (e) => {
  const settings = { fillBots: e.target.value === 'true' };
  if (activeLocalBridge) activeLocalBridge.emit('updateRoomSettings', settings);
  else socket.emit('updateRoomSettings', settings);
});

selectTurnTime?.addEventListener('change', (e) => {
  const settings = { roundTimer: parseInt(e.target.value) };
  if (activeLocalBridge) activeLocalBridge.emit('updateRoomSettings', settings);
  else socket.emit('updateRoomSettings', settings);
});

selectTotalRounds?.addEventListener('change', (e) => {
  const settings = { totalRounds: parseInt(e.target.value) };
  if (activeLocalBridge) activeLocalBridge.emit('updateRoomSettings', settings);
  else socket.emit('updateRoomSettings', settings);
});

const btnAddRoomBot = document.getElementById('btn-add-room-bot');
btnAddRoomBot?.addEventListener('click', () => {
  sounds.playPop();
  if (activeLocalBridge) activeLocalBridge.emit('addBot');
  else socket.emit('addBot');
});

btnReadyToggle?.addEventListener('click', () => {
  if (activeLocalBridge) activeLocalBridge.emit('toggleReady');
  else socket.emit('toggleReady');
  sounds.playPop();
});

btnStartSelectedGame?.addEventListener('click', () => {
  if (activeLocalBridge) {
    activeLocalBridge.emit('selectAndLaunchGame', { gameId: selectedGameId });
  } else if (currentRoom) {
    socket.emit('selectAndLaunchGame', { gameId: selectedGameId });
  }
});

btnBackToRoom?.addEventListener('click', () => {
  sounds.playPop();
  // Remove any open game over modal
  document.querySelector('.universal-gameover-modal')?.remove();

  if (activeLocalBridge) {
    if (activeLocalBridge.room) {
      activeLocalBridge.emit('returnToLobby');
    } else {
      activeLocalBridge.destroy();
      activeLocalBridge = null;
      if (activeGameInstance?.destroy) {
        activeGameInstance.destroy();
        activeGameInstance = null;
      }
      showView('hub');
      renderGameGrid();
    }
  } else if (currentRoom) {
    socket.emit('returnToLobby');
  } else {
    if (activeGameInstance?.destroy) {
      activeGameInstance.destroy();
      activeGameInstance = null;
    }
    showView('hub');
  }
});

// Category Filter Tabs
document.querySelectorAll('.cat-pill').forEach((pill) => {
  pill.addEventListener('click', () => {
    document.querySelectorAll('.cat-pill').forEach(p => p.classList.remove('active'));
    pill.classList.add('active');
    currentCategoryFilter = pill.dataset.cat;
    renderGameGrid();
    sounds.playPop();
  });
});

// Profile Modal Events
btnOpenProfile?.addEventListener('click', () => {
  const prof = avatarManager.getProfile();
  if (profNameInput) profNameInput.value = prof.name;
  renderAvatarPicker(prof.avatar);
  profileModal?.classList.remove('modal-hidden');
});

btnCloseProfile?.addEventListener('click', () => {
  profileModal?.classList.add('modal-hidden');
});

function renderAvatarPicker(currentAvatar) {
  if (!avatarsPickerContainer) return;
  avatarsPickerContainer.innerHTML = '';
  avatarManager.avatars.forEach((av) => {
    const opt = document.createElement('div');
    opt.className = `avatar-opt ${av === currentAvatar ? 'active' : ''}`;
    opt.innerText = av;
    opt.addEventListener('click', () => {
      avatarsPickerContainer.querySelectorAll('.avatar-opt').forEach(o => o.classList.remove('active'));
      opt.classList.add('active');
    });
    avatarsPickerContainer.appendChild(opt);
  });
}

btnSaveProfile?.addEventListener('click', () => {
  const name = profNameInput ? (profNameInput.value.trim() || 'Player') : 'Player';
  const selectedAvEl = avatarsPickerContainer?.querySelector('.avatar-opt.active');
  const avatar = selectedAvEl ? selectedAvEl.innerText : '🦊';

  avatarManager.saveProfile({ name, avatar });
  updateProfileUI();
  profileModal?.classList.add('modal-hidden');
  sounds.playSuccess();
});

// 8. Socket.io Event Handling
socket.on('roomJoined', ({ room, isHost }) => {
  console.log(`🏠 Joined Room: ${room.code} (isHost: ${isHost})`);
  if (isQuickPlayActive) {
    clearInterval(quickPlayInterval);
    quickPlayInterval = null;
    isQuickPlayActive = false;
    if (quickplayModal) quickplayModal.classList.add('modal-hidden');
  }

  currentRoom = room;
  selectedGameId = room.currentGame || 'drawGuess';
  if (roomDisplayCode) roomDisplayCode.innerText = room.code;
  const targetCount = room.settings?.targetPlayers || 4;
  if (roomCountBadge) roomCountBadge.innerText = `👥 ${room.players.length} / ${targetCount} PLAYERS`;

  renderSeats(room.players);
  renderRoomGameSelector();
  updateRoomPlayerSelectOptions(selectedGameId);

  // Apply custom settings if requested on launch
  if (pendingRoomSettings && isHost) {
    socket.emit('updateRoomSettings', pendingRoomSettings);
    pendingRoomSettings = null;
  }

  // If pending instant launch, automatically launch game
  if (pendingLaunchGameId && isHost) {
    const targetGame = pendingLaunchGameId;
    pendingLaunchGameId = null;
    console.log(`🚀 Auto-launching game: ${targetGame}`);
    socket.emit('selectAndLaunchGame', { gameId: targetGame });
    return;
  }

  showView('room');

  if (isHost) {
    if (btnStartSelectedGame) btnStartSelectedGame.style.display = 'block';
    if (btnReadyToggle) btnReadyToggle.style.display = 'none';
    if (hostSettingsPanel) hostSettingsPanel.style.display = 'flex';
    if (hostNotice) hostNotice.innerText = 'You are Host 👑: Configure time/rounds and launch any game!';
  } else {
    if (btnStartSelectedGame) btnStartSelectedGame.style.display = 'none';
    if (btnReadyToggle) btnReadyToggle.style.display = 'block';
    if (hostSettingsPanel) hostSettingsPanel.style.display = 'none';
    if (hostNotice) hostNotice.innerText = 'Waiting for Room Host 👑 to launch a game...';
  }

  sounds.playVictory();
});

socket.on('roomUpdated', ({ room }) => {
  currentRoom = room;
  const targetCount = room.settings?.targetPlayers || 4;
  if (roomCountBadge) roomCountBadge.innerText = `👥 ${room.players.length} / ${targetCount} PLAYERS`;
  renderSeats(room.players);

  if (room.settings) {
    if (selectRoomPlayers) selectRoomPlayers.value = `${room.settings.targetPlayers || 4}`;
    if (selectRoomBots) selectRoomBots.value = `${room.settings.fillBots !== false}`;
    if (selectTurnTime) selectTurnTime.value = `${room.settings.roundTimer}`;
    if (selectTotalRounds) selectTotalRounds.value = `${room.settings.totalRounds}`;
  }
});

socket.on('quickPlayFound', ({ room }) => {
  if (isQuickPlayActive) {
    clearInterval(quickPlayInterval);
    quickPlayInterval = null;
    isQuickPlayActive = false;
    if (quickplayModal) quickplayModal.classList.add('modal-hidden');
    showToast(`🎉 Found active party room ${room.code}! Joining...`, 'success');
    sounds.playSuccess();
  }
});

socket.on('hostMigrated', ({ newHostId, newHost, room }) => {
  currentRoom = room;
  const myId = socket.id;
  const hostId = newHostId || newHost?.id;
  const isHost = hostId === myId;

  if (isHost) {
    showToast('👑 The previous host left. You are now the Room Host!', 'success');
    sounds.playVictory();
    if (btnStartSelectedGame) btnStartSelectedGame.style.display = 'block';
    if (btnReadyToggle) btnReadyToggle.style.display = 'none';
    if (hostSettingsPanel) hostSettingsPanel.style.display = 'flex';
    if (hostNotice) hostNotice.innerText = 'You are Host 👑: Configure time/rounds and launch any game!';
  } else {
    showToast('👑 Room host has migrated to the next active player.', 'info');
  }

  renderSeats(room.players);
});

socket.on('joinError', ({ message }) => {
  showToast(`⚠️ ${message}`, 'error');
});

socket.on('kickedFromRoom', ({ message }) => {
  showToast(`⚠️ ${message || 'You were removed from the room by the Host.'}`, 'error');
  showView('hub');
  renderGameGrid();
});

socket.on('chatMessage', (msg) => {
  chatManager.appendMessage(msg);
  if (roomLayout?.classList.contains('show-stage') && mobileChatBadge) {
    mobileChatBadge.classList.remove('modal-hidden');
  }
});

socket.on('reactionGift', (data) => {
  chatManager.triggerLocalReaction(data.emoji);
});

// Launching Mini-Games (20 Game Factory via Server)
socket.on('gameLaunched', ({ gameId, room }) => {
  console.log(`🎮 Game Launched Event: ${gameId}`);
  currentRoom = room;
  launchGameClient(gameId, socket);
});

socket.on('returnedToLobby', ({ room }) => {
  currentRoom = room;
  if (activeGameInstance?.destroy) {
    activeGameInstance.destroy();
    activeGameInstance = null;
  }
  showView('room');
  renderSeats(room.players);
  sounds.playChime();
});

// Auto-Join Room from Viral Deep Link (?room=CODE or ?r=CODE)
const roomParam = urlParams.get('room') || urlParams.get('r');
if (roomParam) {
  // Seamless viral invite onboarding: generate guest profile if first time visitor
  if (!localStorage.getItem('wyhsiar_player_profile') && !localStorage.getItem('wyhsiar_name')) {
    const defaultAvatars = ['🦊', '🐱', '🐶', '🐼', '🐯', '🦁', '🐵', '🐸', '🦄', '🚀', '⭐', '🌈'];
    const randomAv = defaultAvatars[Math.floor(Math.random() * defaultAvatars.length)];
    const randomNum = Math.floor(1000 + Math.random() * 9000);
    const guestName = `Guest_${randomNum}`;
    avatarManager.saveProfile({ name: guestName, avatar: randomAv });
    updateProfileUI();
  }
  setTimeout(() => joinPartyRoom(roomParam), 450);
}

// Initial Render
renderGameGrid();